import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../utils/api';
import {
  DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import RichTextEditor from '../../components/RichTextEditor';
import { toast } from 'sonner';
import {
  Plus, Edit, Trash2, GripVertical, Save, ArrowLeft, RefreshCw,
  Image, Type, LayoutGrid, BarChart2, Megaphone, Phone, Eye
} from 'lucide-react';

// ── block type registry ─────────────────────────────────────────────────────────
const BLOCK_TYPES = {
  hero:         { label: 'Hero / Banner',    icon: Image,       color: 'bg-blue-500/10 border-blue-200' },
  features:     { label: 'Características',  icon: LayoutGrid,  color: 'bg-green-500/10 border-green-200' },
  stats:        { label: 'Estadísticas',     icon: BarChart2,   color: 'bg-amber-500/10 border-amber-200' },
  rich_text:    { label: 'Texto / Contenido',icon: Type,        color: 'bg-purple-500/10 border-purple-200' },
  cta:          { label: 'Llamada a acción', icon: Megaphone,   color: 'bg-red-500/10 border-red-200' },
  contact_info: { label: 'Info Contacto',    icon: Phone,       color: 'bg-teal-500/10 border-teal-200' },
};

// ── block editors ───────────────────────────────────────────────────────────────
const HeroEditor = ({ data, onChange }) => (
  <div className="space-y-3">
    {[['title','Título principal'],['subtitle','Subtítulo'],['description','Descripción']].map(([k,l]) => (
      <div key={k} className="space-y-1">
        <Label className="text-xs">{l}</Label>
        <Input value={data[k]||''} onChange={e=>onChange({...data,[k]:e.target.value})} />
      </div>
    ))}
    <div className="grid grid-cols-2 gap-3">
      {[['cta_text','Botón principal (texto)'],['cta_link','Botón principal (link)'],['cta_secondary_text','Botón secundario (texto)'],['cta_secondary_link','Botón secundario (link)']].map(([k,l]) => (
        <div key={k} className="space-y-1">
          <Label className="text-xs">{l}</Label>
          <Input value={data[k]||''} onChange={e=>onChange({...data,[k]:e.target.value})} placeholder={k.includes('link')?'/ruta':''} />
        </div>
      ))}
    </div>
  </div>
);

const FeaturesEditor = ({ data, onChange }) => {
  const items = data.items || [];
  const updateItem = (i, field, val) => { const a=[...items]; a[i]={...a[i],[field]:val}; onChange({...data,items:a}); };
  const addItem = () => onChange({...data, items:[...items,{icon:'Star',title:'',description:''}]});
  const removeItem = (i) => onChange({...data, items:items.filter((_,idx)=>idx!==i)});
  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <Label className="text-xs">Título de la sección</Label>
        <Input value={data.title||''} onChange={e=>onChange({...data,title:e.target.value})} />
      </div>
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-xs">Características</Label>
          <Button type="button" size="sm" variant="outline" onClick={addItem}><Plus className="w-3 h-3 mr-1"/>Agregar</Button>
        </div>
        {items.map((item,i) => (
          <div key={i} className="p-3 border rounded-lg bg-muted/20 space-y-2">
            <div className="grid grid-cols-3 gap-2">
              <div className="space-y-1"><Label className="text-xs">Icono (lucide)</Label><Input value={item.icon||''} onChange={e=>updateItem(i,'icon',e.target.value)} placeholder="BookOpen" /></div>
              <div className="space-y-1 col-span-2"><Label className="text-xs">Título</Label><Input value={item.title||''} onChange={e=>updateItem(i,'title',e.target.value)} /></div>
            </div>
            <div className="flex gap-2">
              <Input value={item.description||''} onChange={e=>updateItem(i,'description',e.target.value)} placeholder="Descripción..." className="flex-1 text-sm" />
              <Button type="button" variant="ghost" size="sm" onClick={()=>removeItem(i)} className="text-destructive shrink-0"><Trash2 className="w-4 h-4"/></Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const StatsEditor = ({ data, onChange }) => {
  const items = data.items || [];
  const updateItem = (i, field, val) => { const a=[...items]; a[i]={...a[i],[field]:val}; onChange({...data,items:a}); };
  const addItem = () => onChange({...data, items:[...items,{value:'0',label:''}]});
  const removeItem = (i) => onChange({...data, items:items.filter((_,idx)=>idx!==i)});
  return (
    <div className="space-y-3">
      <div className="space-y-1"><Label className="text-xs">Título</Label><Input value={data.title||''} onChange={e=>onChange({...data,title:e.target.value})} /></div>
      <div className="space-y-2">
        <div className="flex items-center justify-between"><Label className="text-xs">Estadísticas</Label><Button type="button" size="sm" variant="outline" onClick={addItem}><Plus className="w-3 h-3 mr-1"/>Agregar</Button></div>
        {items.map((item,i) => (
          <div key={i} className="flex gap-2 items-center">
            <Input value={item.value||''} onChange={e=>updateItem(i,'value',e.target.value)} placeholder="500+" className="w-24 text-center font-bold" />
            <Input value={item.label||''} onChange={e=>updateItem(i,'label',e.target.value)} placeholder="Estudiantes..." className="flex-1" />
            <Button type="button" variant="ghost" size="sm" onClick={()=>removeItem(i)} className="text-destructive shrink-0"><Trash2 className="w-4 h-4"/></Button>
          </div>
        ))}
      </div>
    </div>
  );
};

const RichTextBlockEditor = ({ data, onChange }) => (
  <div className="space-y-3">
    <div className="space-y-1"><Label className="text-xs">Título de la sección</Label><Input value={data.title||''} onChange={e=>onChange({...data,title:e.target.value})} /></div>
    <div className="space-y-1"><Label className="text-xs">Contenido</Label><RichTextEditor value={data.content||''} onChange={v=>onChange({...data,content:v})} minHeight={180}/></div>
  </div>
);

const CTAEditor = ({ data, onChange }) => (
  <div className="space-y-3">
    {[['title','Título'],['subtitle','Subtítulo'],['button_text','Texto del botón'],['button_link','Link del botón']].map(([k,l]) => (
      <div key={k} className="space-y-1"><Label className="text-xs">{l}</Label><Input value={data[k]||''} onChange={e=>onChange({...data,[k]:e.target.value})} /></div>
    ))}
  </div>
);

const ContactInfoEditor = ({ data, onChange }) => (
  <div className="space-y-3">
    {[['title','Título'],['description','Descripción'],['email','Email'],['phone','Teléfono'],['address','Dirección']].map(([k,l]) => (
      <div key={k} className="space-y-1"><Label className="text-xs">{l}</Label><Input value={data[k]||''} onChange={e=>onChange({...data,[k]:e.target.value})} /></div>
    ))}
  </div>
);

const BLOCK_EDITORS = { hero: HeroEditor, features: FeaturesEditor, stats: StatsEditor, rich_text: RichTextBlockEditor, cta: CTAEditor, contact_info: ContactInfoEditor };

// ── sortable block card ─────────────────────────────────────────────────────────
const SortableBlock = ({ block, onEdit, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: block.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 };
  const meta = BLOCK_TYPES[block.type] || { label: block.type, icon: Type, color: 'bg-muted' };
  const BlockIcon = meta.icon;
  return (
    <div ref={setNodeRef} style={style} className={`border rounded-xl p-3 flex items-center gap-3 ${meta.color} ${isDragging ? 'shadow-lg z-10 relative' : ''}`} data-testid={`block-${block.id}`}>
      <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing text-muted-foreground/40 hover:text-muted-foreground shrink-0 p-1" title="Arrastrar"><GripVertical className="w-4 h-4"/></button>
      <BlockIcon className="w-4 h-4 shrink-0 text-muted-foreground" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold truncate">{meta.label}</p>
        <p className="text-xs text-muted-foreground truncate">{block.data?.title || block.data?.email || '—'}</p>
      </div>
      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 shrink-0" onClick={()=>onEdit(block)} data-testid={`edit-block-${block.id}`}><Edit className="w-3.5 h-3.5"/></Button>
      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 shrink-0 text-destructive hover:text-destructive" onClick={()=>onDelete(block.id)} data-testid={`delete-block-${block.id}`}><Trash2 className="w-3.5 h-3.5"/></Button>
    </div>
  );
};

// ── page editor ─────────────────────────────────────────────────────────────────
const PAGE_SLUGS = [
  { slug: 'home', label: 'Página de Inicio', description: 'Hero, características, estadísticas y CTA' },
  { slug: 'about', label: 'Nosotros', description: 'Historia, misión y visión' },
  { slug: 'contact', label: 'Contacto', description: 'Información de contacto' },
];

const PageEditor = ({ slug, onBack }) => {
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editDialog, setEditDialog] = useState({ open: false, block: null });
  const [addDialog, setAddDialog] = useState(false);
  const [selectedNewType, setSelectedNewType] = useState('hero');

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    loadPage();
  }, [slug]);

  const loadPage = async () => {
    try {
      const res = await api.get(`/pages/${slug}`);
      const content = res.data?.content || {};
      setBlocks(content.blocks || []);
    } catch (err) {
      if (err.response?.status === 404) setBlocks([]);
      else toast.error('Error al cargar la página');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const reordered = blocks.map((b, i) => ({ ...b, order: i + 1 }));
      await api.put(`/pages/${slug}/blocks`, { content: { blocks: reordered } });
      setBlocks(reordered);
      toast.success('¡Página guardada exitosamente!');
    } catch { toast.error('Error al guardar'); }
    finally { setSaving(false); }
  };

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIdx = blocks.findIndex(b => b.id === active.id);
    const newIdx = blocks.findIndex(b => b.id === over.id);
    setBlocks(arrayMove(blocks, oldIdx, newIdx));
  };

  const handleAddBlock = () => {
    const newBlock = { id: Date.now().toString(), type: selectedNewType, order: blocks.length + 1, data: {} };
    setBlocks(prev => [...prev, newBlock]);
    setAddDialog(false);
    setEditDialog({ open: true, block: newBlock });
    toast.success('Bloque agregado - edítalo ahora');
  };

  const handleSaveBlock = (updatedData) => {
    setBlocks(prev => prev.map(b => b.id === editDialog.block?.id ? { ...b, data: updatedData } : b));
    setEditDialog({ open: false, block: null });
  };

  const handleDeleteBlock = (blockId) => {
    if (!window.confirm('¿Eliminar este bloque?')) return;
    setBlocks(prev => prev.filter(b => b.id !== blockId));
  };

  if (loading) return <div className="p-8 text-center text-muted-foreground">Cargando...</div>;

  const editingBlock = editDialog.block ? blocks.find(b => b.id === editDialog.block.id) : null;
  const BlockEditorComponent = editingBlock ? BLOCK_EDITORS[editingBlock.type] : null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <Button variant="ghost" size="sm" onClick={onBack} className="gap-1 -ml-2 mb-2 text-muted-foreground"><ArrowLeft className="w-4 h-4"/>Páginas</Button>
          <h2 className="text-xl font-bold">{PAGE_SLUGS.find(p=>p.slug===slug)?.label || slug}</h2>
          <p className="text-sm text-muted-foreground">{blocks.length} bloque(s) · Arrastra para reordenar</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => window.open('/', '_blank')} className="gap-2"><Eye className="w-4 h-4"/>Ver sitio</Button>
          <Button onClick={handleSave} disabled={saving} className="gap-2" data-testid="save-page-btn">
            <Save className="w-4 h-4"/>{saving ? 'Guardando...' : 'Guardar Cambios'}
          </Button>
        </div>
      </div>

      {/* Block list */}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={blocks.map(b => b.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-2">
            {blocks.length === 0 && (
              <div className="p-8 border-2 border-dashed rounded-xl text-center text-muted-foreground">
                <LayoutGrid className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <p className="text-sm">Sin bloques. Agrega el primero.</p>
              </div>
            )}
            {blocks.map(block => (
              <SortableBlock key={block.id} block={block} onEdit={b=>setEditDialog({open:true,block:b})} onDelete={handleDeleteBlock} />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {/* Add block */}
      <Button variant="outline" className="w-full gap-2 border-dashed" onClick={() => setAddDialog(true)} data-testid="add-block-btn">
        <Plus className="w-4 h-4"/>Agregar Bloque
      </Button>

      {/* Add block dialog */}
      <Dialog open={addDialog} onOpenChange={setAddDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Seleccionar tipo de bloque</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-2 py-2">
            {Object.entries(BLOCK_TYPES).map(([type, meta]) => {
              const Icon = meta.icon;
              return (
                <button key={type} onClick={()=>setSelectedNewType(type)}
                  className={`p-3 rounded-lg border text-left transition-all ${selectedNewType===type ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'hover:border-muted-foreground/30'}`}>
                  <Icon className="w-5 h-5 mb-1 text-muted-foreground" />
                  <p className="text-xs font-semibold">{meta.label}</p>
                </button>
              );
            })}
          </div>
          <Button onClick={handleAddBlock} className="w-full">Agregar {BLOCK_TYPES[selectedNewType]?.label}</Button>
        </DialogContent>
      </Dialog>

      {/* Edit block dialog */}
      {editingBlock && BlockEditorComponent && (
        <Dialog open={editDialog.open} onOpenChange={o => !o && setEditDialog({open:false,block:null})}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Editar: {BLOCK_TYPES[editingBlock.type]?.label}</DialogTitle></DialogHeader>
            <EditBlockForm
              key={editingBlock.id}
              block={editingBlock}
              EditorComponent={BlockEditorComponent}
              onSave={handleSaveBlock}
              onClose={() => setEditDialog({open:false,block:null})}
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

// Separate component to manage local state for block editing
const EditBlockForm = ({ block, EditorComponent, onSave, onClose }) => {
  const [localData, setLocalData] = useState(block.data || {});
  return (
    <div className="space-y-4 py-2">
      <EditorComponent data={localData} onChange={setLocalData} />
      <div className="flex gap-2 pt-2">
        <Button onClick={() => onSave(localData)} data-testid="save-block-btn"><Save className="w-4 h-4 mr-2"/>Guardar bloque</Button>
        <Button variant="outline" onClick={onClose}>Cancelar</Button>
      </div>
    </div>
  );
};

// ── main CMS manager ────────────────────────────────────────────────────────────
const ManageCMS = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [activePage, setActivePage] = useState(null);
  const [initializing, setInitializing] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user || !['superadmin', 'admin'].includes(user.role)) navigate('/dashboard');
  }, [user, authLoading, navigate]);

  const handleInitialize = async () => {
    setInitializing(true);
    try {
      const res = await api.post('/pages/initialize-defaults');
      toast.success(res.data.message);
    } catch { toast.error('Error al inicializar'); }
    finally { setInitializing(false); }
  };

  if (!user) return null;

  if (activePage) {
    return (
      <div className="min-h-screen bg-secondary" data-testid="cms-page-editor">
        <div className="max-w-3xl mx-auto px-4 py-8">
          <PageEditor slug={activePage} onBack={() => setActivePage(null)} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary" data-testid="cms-manager-page">
      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
          <div>
            <Link to="/dashboard">
              <Button variant="ghost" size="sm" className="gap-1 -ml-2 mb-3 text-muted-foreground"><ArrowLeft className="w-4 h-4"/>Dashboard</Button>
            </Link>
            <h1 className="text-2xl font-bold">Gestor de Contenido (CMS)</h1>
            <p className="text-sm text-muted-foreground mt-1">Edita el contenido de las páginas públicas del sitio</p>
          </div>
          <Button variant="outline" onClick={handleInitialize} disabled={initializing} className="gap-2" data-testid="initialize-pages-btn">
            <RefreshCw className={`w-4 h-4 ${initializing ? 'animate-spin' : ''}`} />
            {initializing ? 'Inicializando...' : 'Inicializar páginas por defecto'}
          </Button>
        </div>

        {/* Page cards */}
        <div className="space-y-3">
          {PAGE_SLUGS.map(page => (
            <Card key={page.slug} className="cursor-pointer hover:border-primary/40 transition-colors" onClick={() => setActivePage(page.slug)} data-testid={`cms-page-${page.slug}`}>
              <CardContent className="p-5 flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <LayoutGrid className="w-5 h-5 text-primary" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold">{page.label}</h3>
                  <p className="text-sm text-muted-foreground">{page.description}</p>
                </div>
                <Button size="sm" variant="outline" className="gap-1 shrink-0">
                  <Edit className="w-3.5 h-3.5"/>Editar
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ManageCMS;
